/**
 * Filter evaluation — decide whether a row passes a contract filter.
 *
 * Distinct from `filter-ast.ts`, which *translates* between our AST and AG Grid's
 * filter model. This one evaluates directly, at full fidelity, and is what makes
 * the translation optional: Community's model cannot express mixed AND/OR nesting
 * or a third condition on one column, so a saved filter can be richer than the
 * grid could redisplay. Evaluating it ourselves removes that ceiling entirely.
 *
 * One entry point, `matchesFilter`, serves both consumers:
 * - `doesExternalFilterPass` in `ScreenerGrid`, for the section's base filter
 * - `cellStyleFor` tier 2, for a format rule tested against the whole row
 *
 * `testPredicate` lives here rather than in `columns.ts` because leaf comparison
 * is filter semantics, not column building — and having it here keeps the
 * dependency one-way (`columns` -> `filter-eval` -> `expr`) instead of the cycle
 * it would otherwise form. One implementation means a format rule and a filter
 * can never disagree about what `between` or `contains` means.
 */

import type {
  ScreenerExpr,
  ScreenerFilterCondition,
  ScreenerFilterGroup,
  ScreenerFilterNode,
  ScreenerPredicate,
} from "@qufolio/model";
import type { ScreenerRow } from "@qufolio/transport";

import {
  collectFields as collectExprFields,
  evaluateAsBoolean,
  needsHistory,
  printExpr,
  type EvalOptions,
} from "./expr.js";

/** A `ScreenerConfig.filter`: the structured default, or the expression escape hatch. */
export type ScreenerFilter = ScreenerFilterNode | ScreenerExpr;

/** Evaluate a field-less predicate against one cell value. */
export function testPredicate(predicate: ScreenerPredicate, value: unknown): boolean {
  const { op } = predicate;
  if (op === "blank") return value == null || value === "";
  if (op === "notBlank") return !(value == null || value === "");
  if (value == null) return false;

  const target = predicate.value;
  const text = typeof value === "string" ? value.toLowerCase() : String(value).toLowerCase();
  const targetText = typeof target === "string" ? target.toLowerCase() : String(target).toLowerCase();

  switch (op) {
    case "eq":
      return value === target;
    case "ne":
      return value !== target;
    case "gt":
      return Number(value) > Number(target);
    case "gte":
      return Number(value) >= Number(target);
    case "lt":
      return Number(value) < Number(target);
    case "lte":
      return Number(value) <= Number(target);
    case "between":
      return Number(value) >= Number(target) && Number(value) <= Number(predicate.valueTo);
    case "contains":
      return text.includes(targetText);
    case "notContains":
      return !text.includes(targetText);
    case "startsWith":
      return text.startsWith(targetText);
    case "endsWith":
      return text.endsWith(targetText);
    case "in":
      return (predicate.values ?? []).some((v) => v === value);
    default:
      return false;
  }
}

const isGroup = (node: ScreenerFilter): node is ScreenerFilterGroup =>
  "children" in node && Array.isArray((node as ScreenerFilterGroup).children);

/**
 * Both filter shapes are objects, so they are told apart structurally.
 * `ScreenerExpr` nodes are the only ones carrying `kind`.
 */
export function isExpr(filter: ScreenerFilter): filter is ScreenerExpr {
  return "kind" in filter;
}

// ── conditions written directly ─────────────────────────────────────────────
//
// A ScanScript condition (owner, 2026-10-05; docs/plans/pattern-screener.md)
// that reads bar history cannot be evaluated against one row — `c > sma(c, 50)`
// is null there, and a null condition filters everything out. So it is sent to
// the server as a hidden computed field (`historyConditions`, read by the
// app's computed request) and evaluated here by reading that field. The field
// name is a hash of the printed expression: the same condition is the same
// field wherever it appears, and nothing about it is stored.

/** The prefix of a condition's hidden field — never offered as a column. */
export const CONDITION_FIELD_PREFIX = "cond_";

/** FNV-1a, base 36. Stable, short, collision-tolerant enough. */
function fnv1a36(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** A history condition's hidden field: the hash of its printed expression. */
export function conditionField(expr: ScreenerExpr): string {
  return `${CONDITION_FIELD_PREFIX}${fnv1a36(printExpr(expr))}`;
}

// ── sealed conditions ───────────────────────────────────────────────────────
//
// A group screen whose conditions are sealed (docs/plans/screen-links.md) sends
// a member each one as a name with **no rule**. Ticking it asks the server for
// a pass/fail field by `{screen, id}` — the server looks the rule up in the
// published screen — and that field is read exactly as a history condition's
// is. The browser mints the field name, so it never needs the rule to do it.
//
// `cond_s_` and not `cond_s`: a history field is `cond_` plus base 36, which
// can begin with `s` but never with `_`, so the two kinds cannot collide and a
// field can be told apart by its name.

/** The prefix of a sealed condition's hidden field — inside `CONDITION_FIELD_PREFIX`. */
export const SEALED_FIELD_PREFIX = `${CONDITION_FIELD_PREFIX}s_`;

/** A sealed condition's hidden field: the hash of the screen it belongs to and its id. */
export function sealedConditionField(screen: string, id: string): string {
  return `${SEALED_FIELD_PREFIX}${fnv1a36(`${screen}:${id}`)}`;
}

/**
 * A server-computed pass/fail flag, read off one row.
 *
 * Absent until the server's answer arrives: not yet true, so the row waits
 * rather than passing on a condition nobody has evaluated. One rule for both
 * kinds of hidden field, so a sealed condition and a history one cannot
 * disagree about what "true" is.
 */
function flagIsTrue(value: unknown): boolean {
  return value === true || (typeof value === "number" && value !== 0 && Number.isFinite(value));
}

/** Every history-needing expression in a filter, once each, with its hidden field. */
export function historyConditions(
  filter: ScreenerFilter | undefined,
): { field: string; expression: ScreenerExpr }[] {
  const out = new Map<string, ScreenerExpr>();
  const walk = (node: ScreenerFilter) => {
    if (isExpr(node)) {
      if (needsHistory(node)) out.set(conditionField(node), node);
    } else if (isGroup(node)) {
      node.children.forEach(walk);
    }
  };
  if (filter) walk(filter);
  return [...out].map(([field, expression]) => ({ field, expression }));
}

/** One expression condition against one row — from its hidden field when it reads history. */
function evaluateExprCondition(expr: ScreenerExpr, row: ScreenerRow, options: EvalOptions = {}): boolean {
  if (!needsHistory(expr)) return evaluateAsBoolean(expr, row, options);
  return flagIsTrue(row[conditionField(expr)]);
}

/** Evaluate a structured filter node against one row. */
export function evaluateFilterNode(node: ScreenerFilterNode, row: ScreenerRow): boolean {
  if (isExpr(node)) return evaluateExprCondition(node, row);
  if (isGroup(node)) {
    // An empty group is vacuously true rather than excluding everything — a
    // half-built group in the builder must not blank the grid mid-edit.
    if (node.children.length === 0) return true;
    return node.op === "any"
      ? node.children.some((child) => evaluateFilterNode(child, row))
      : node.children.every((child) => evaluateFilterNode(child, row));
  }

  const condition = node as ScreenerFilterCondition;
  // A sealed condition's flag, from `withSealedConditions` — the flag rule, not
  // `eq true`, so a server that answers 1 is read the way a history field is.
  if (condition.field.startsWith(SEALED_FIELD_PREFIX)) return flagIsTrue(row[condition.field]);
  // The condition minus its field is exactly a predicate, which is why tier 1
  // and tier 2 share one comparison implementation.
  const predicate: ScreenerPredicate = {
    op: condition.op,
    ...(condition.value !== undefined ? { value: condition.value } : {}),
    ...(condition.valueTo !== undefined ? { valueTo: condition.valueTo } : {}),
    ...(condition.values !== undefined ? { values: condition.values } : {}),
  };
  return testPredicate(predicate, row[condition.field]);
}

/** Does this row pass the filter? Handles both arms of `ScreenerConfig.filter`. */
export function matchesFilter(
  filter: ScreenerFilter | undefined,
  row: ScreenerRow,
  options: EvalOptions = {},
): boolean {
  if (!filter) return true;
  return isExpr(filter)
    ? evaluateExprCondition(filter, row, options)
    : evaluateFilterNode(filter, row);
}

/** Count matching rows — the filter bar's match count, free once rows are evaluated. */
export function countMatches(
  filter: ScreenerFilter | undefined,
  rows: readonly ScreenerRow[],
): number {
  if (!filter) return rows.length;
  let n = 0;
  for (const row of rows) if (matchesFilter(filter, row)) n += 1;
  return n;
}

/** Every field a filter reads, for the `requires[]` compatibility check. */
export function filterFields(
  filter: ScreenerFilter | undefined,
  into: Set<string> = new Set(),
): Set<string> {
  if (!filter) return into;
  if (isExpr(filter)) {
    // Delegating keeps one definition of "which fields does this read".
    for (const f of collectExprFields(filter)) into.add(f);
    return into;
  }
  if (isGroup(filter)) {
    for (const child of filter.children) filterFields(child, into);
  } else if (isExpr(filter)) {
    for (const f of collectExprFields(filter)) into.add(f);
  } else {
    into.add((filter as ScreenerFilterCondition).field);
  }
  return into;
}

/**
 * A one-line, readable rendering of a filter — the filter bar's summary.
 *
 * Deliberately plain text rather than the DSL: the bar must stay legible at a
 * glance for a filter the builder produced, and a user reading it has not
 * necessarily learned the DSL yet.
 */
export function summarizeFilter(filter: ScreenerFilter | undefined): string {
  if (!filter) return "No condition";
  // The expression arm already has a printer; reuse it rather than write a second.
  if (isExpr(filter)) return printExpr(filter);
  return summarizeNode(filter);
}

function summarizeNode(node: ScreenerFilterNode, depth = 0): string {
  if (isExpr(node)) return printExpr(node);
  if (isGroup(node)) {
    if (node.children.length === 0) return "";
    const joiner = node.op === "any" ? " or " : " and ";
    const body = node.children
      .map((child) => summarizeNode(child, depth + 1))
      .filter(Boolean)
      .join(joiner);
    return depth > 0 && node.children.length > 1 ? `(${body})` : body;
  }

  const c = node as ScreenerFilterCondition;
  // Its field is a hash, and the rule behind it is the one thing not to print.
  if (c.field.startsWith(SEALED_FIELD_PREFIX)) return "a sealed condition";
  const value = (v: unknown) => (typeof v === "string" ? `"${v}"` : String(v));
  switch (c.op) {
    case "between":
      return `${c.field} between ${value(c.value)} and ${value(c.valueTo)}`;
    case "in":
      return `${c.field} in [${(c.values ?? []).map(value).join(", ")}]`;
    case "blank":
      return `${c.field} is blank`;
    case "notBlank":
      return `${c.field} is not blank`;
    default:
      return `${c.field} ${OP_TEXT[c.op] ?? c.op} ${value(c.value)}`;
  }
}

const OP_TEXT: Record<string, string> = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  contains: "contains",
  notContains: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
};
